/**
 * @module composables/useDashboardData
 * @description 대시보드 데이터 fetch 및 상태 관리 composable.
 * useDashboardFilter에서 제공하는 필터 상태를 기반으로
 * AS 현황, 서비스품질, SLA 준수율, 파트너 KPI, 지역 히트맵 데이터를 조회합니다.
 * 필터 변경 시 자동으로 전체 데이터를 갱신합니다.
 *
 * 제거 이력: 트렌드 데이터 fetchTrend (SURINP-289) — 대시보드 위젯 제거에 따라 함께 제거.
 */
import { ref, watch } from 'vue'
import {
  getRepairRequestByStatusToDashboard,
  getRepairRequestByStatusAndBetweenDateToDashboard,
  getServiceQualityToDashboard,
  getServiceQualityBYBetweenDateToDashboard,
  getSlaCompliance,
  getSlaComplianceBetween,
  getPartnerKpi,
  getPartnerKpiBetween,
  getRegionHeatmap,
  getRegionHeatmapBetween,
  getDashboardTodaySummary,
  getCustomerStatus
} from '@/api/domain/dashboard'

// ── 기본값 상수 (Default Values) ──

/** AS 현황 초기 상태 */
const DEFAULT_REPAIR_STATUS = {
  summation: '0',
  request: '0',
  assignment: '0',
  visitWait: '0',
  visit: '0',
  delay: '0',
  revisit: '0',
  complete: '0',
  repair: '0'
}

/** 서비스품질 초기 상태 */
const DEFAULT_SERVICE_QUALITY = {
  productModelServiceQualitys: [],
  partnerServiceQualitys: []
}

/** TODAY 요약 (Hero Panel) 초기 상태 */
const DEFAULT_TODAY_SUMMARY = {
  receipt: 0,
  complete: 0,
  inProgress: 0,
  unassigned: 0,
  slaImminent: 0
}

/**
 * 대시보드 데이터를 fetch하고 관리하는 composable입니다.
 * useDashboardFilter가 반환하는 필터 객체를 인자로 받아,
 * 필터 상태에 따라 적절한 API를 호출합니다.
 *
 * @param {{
 *   periodStatus: import('vue').Ref<string>,
 *   fromDate: import('vue').Ref<Date|null>,
 *   toDate: import('vue').Ref<Date|null>,
 *   partnerId: import('vue').Ref<number|null>,
 *   isCustomDateRange: import('vue').ComputedRef<boolean>
 * }} filter - useDashboardFilter()가 반환하는 필터 객체
 *
 * @returns {{
 *   repairStatus: import('vue').Ref<object>,
 *   serviceQuality: import('vue').Ref<object>,
 *   slaCompliance: import('vue').Ref<object|null>,
 *   partnerKpi: import('vue').Ref<Array>,
 *   regionHeatmap: import('vue').Ref<Array>,
 *   loading: import('vue').Ref<boolean>,
 *   fetchRepairStatus: () => Promise<void>,
 *   fetchServiceQuality: () => Promise<void>,
 *   fetchSlaCompliance: () => Promise<void>,
 *   fetchPartnerKpi: () => Promise<void>,
 *   fetchRegionHeatmap: () => Promise<void>,
 *   fetchAll: () => Promise<void>
 * }}
 */
export function useDashboardData(filter) {
  // ── 상태 (State) ──

  /** AS 현황 데이터 */
  const repairStatus = ref({ ...DEFAULT_REPAIR_STATUS })

  /** 서비스품질 데이터 */
  const serviceQuality = ref({ ...DEFAULT_SERVICE_QUALITY })

  /** SLA 준수율 데이터 */
  const slaCompliance = ref(null)

  /** 파트너별 KPI 데이터 */
  const partnerKpi = ref([])

  /** 지역별 히트맵 데이터 */
  const regionHeatmap = ref([])

  /** TODAY 요약 (Hero Panel용) 데이터 */
  const todaySummary = ref({ ...DEFAULT_TODAY_SUMMARY })

  /** 고객사별 5메트릭 (CustomerStatusListPanel용) 데이터 */
  const customerStatus = ref([])

  /** 데이터 로딩 상태 — 최초 fetch 시에만 true. 폴링/필터 갱신 시에는 깜빡임 방지 위해 loading을 끄지 않음. */
  const loading = ref(false)

  /** 최초 fetchAll 완료 여부. true 이후로는 loading을 활성화하지 않는다. */
  const hasFetchedOnce = ref(false)

  // ── 내부 헬퍼 (Internal Helpers) ──

  /**
   * 현재 필터 기반으로 날짜 범위 쿼리 객체를 생성합니다.
   *
   * @returns {{ startDate: string, endDate: string }} 날짜 범위 쿼리 객체
   */
  const buildDateRangeQuery = () => {
    const formatDate = (d) => {
      if (!d) return ''
      const date = new Date(d)
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    }
    return {
      startDate: formatDate(filter.fromDate.value),
      endDate: formatDate(filter.toDate.value)
    }
  }

  // ── 개별 fetch 함수 (Individual Fetch Functions) ──

  /**
   * AS 현황 데이터를 조회합니다.
   * 사용자 지정 날짜 범위 여부에 따라 다른 API를 호출합니다.
   *
   * @returns {Promise<void>}
   */
  async function fetchRepairStatus() {
    try {
      let response
      if (filter.isCustomDateRange.value) {
        response = await getRepairRequestByStatusAndBetweenDateToDashboard(buildDateRangeQuery())
      } else {
        response = await getRepairRequestByStatusToDashboard(filter.periodStatus.value)
      }
      if (response) {
        repairStatus.value = response
      }
    } catch (error) {
      window.__APP_LOG__(error)
      repairStatus.value = { ...DEFAULT_REPAIR_STATUS }
    }
  }

  /**
   * 서비스품질 데이터를 조회합니다.
   * 사용자 지정 날짜 범위 여부에 따라 다른 API를 호출합니다.
   *
   * @returns {Promise<void>}
   */
  async function fetchServiceQuality() {
    try {
      let response
      if (filter.isCustomDateRange.value) {
        response = await getServiceQualityBYBetweenDateToDashboard(buildDateRangeQuery())
      } else {
        response = await getServiceQualityToDashboard(filter.periodStatus.value)
      }
      if (response) {
        serviceQuality.value = response
      }
    } catch (error) {
      window.__APP_LOG__(error)
      serviceQuality.value = { ...DEFAULT_SERVICE_QUALITY }
    }
  }

  /**
   * SLA 준수율 데이터를 조회합니다.
   *
   * @returns {Promise<void>}
   */
  async function fetchSlaCompliance() {
    try {
      let response
      if (filter.isCustomDateRange.value) {
        response = await getSlaComplianceBetween(buildDateRangeQuery())
      } else {
        response = await getSlaCompliance(filter.periodStatus.value)
      }
      if (response) {
        slaCompliance.value = response
      }
    } catch (error) {
      window.__APP_LOG__(error)
      slaCompliance.value = null
    }
  }

  /**
   * 파트너별 KPI 데이터를 조회합니다.
   *
   * @returns {Promise<void>}
   */
  async function fetchPartnerKpi() {
    try {
      let response
      if (filter.isCustomDateRange.value) {
        response = await getPartnerKpiBetween(buildDateRangeQuery())
      } else {
        response = await getPartnerKpi(filter.periodStatus.value)
      }
      if (response) {
        partnerKpi.value = response
      }
    } catch (error) {
      window.__APP_LOG__(error)
      partnerKpi.value = []
    }
  }

  /**
   * 지역별 히트맵 데이터를 조회합니다.
   *
   * @returns {Promise<void>}
   */
  async function fetchRegionHeatmap() {
    try {
      let response
      if (filter.isCustomDateRange.value) {
        response = await getRegionHeatmapBetween(buildDateRangeQuery())
      } else {
        response = await getRegionHeatmap(filter.periodStatus.value)
      }
      if (response) {
        regionHeatmap.value = response
      }
    } catch (error) {
      window.__APP_LOG__(error)
      regionHeatmap.value = []
    }
  }

  /**
   * TODAY 요약(접수/완료/진행/미배정)을 조회합니다. (Hero Panel)
   * SURINP-182 신규 API. 응답 실패 시 0으로 폴백.
   *
   * @returns {Promise<void>}
   */
  async function fetchTodaySummary() {
    try {
      const response = await getDashboardTodaySummary()
      if (response) {
        todaySummary.value = {
          receipt: Number(response.receipt) || 0,
          complete: Number(response.complete) || 0,
          inProgress: Number(response.inProgress) || 0,
          unassigned: Number(response.unassigned) || 0,
          slaImminent: Number(response.slaImminent) || 0
        }
      }
    } catch (error) {
      window.__APP_LOG__(error)
      todaySummary.value = { ...DEFAULT_TODAY_SUMMARY }
    }
  }

  /**
   * 고객사별 5메트릭(접수/대기/취소/완료/완료율)을 조회합니다.
   * 메인 필터의 periodStatus에 종속(D5-3 단일 source).
   *
   * @returns {Promise<void>}
   */
  async function fetchCustomerStatus() {
    try {
      const response = await getCustomerStatus(filter.periodStatus.value)
      if (response && Array.isArray(response.items)) {
        customerStatus.value = response.items
      } else if (Array.isArray(response)) {
        customerStatus.value = response
      } else {
        customerStatus.value = []
      }
    } catch (error) {
      window.__APP_LOG__(error)
      customerStatus.value = []
    }
  }

  // ── 전체 데이터 갱신 (Fetch All) ──

  /**
   * 모든 대시보드 데이터를 병렬로 조회합니다.
   * 기존 API(repair-status, service-quality)는 항상 호출하고,
   * 신규 API(SURINP-182 의존)는 별도로 호출하여 실패해도 기존 데이터에 영향 없음.
   *
   * @returns {Promise<void>}
   */
  async function fetchAll() {
    const isFirstFetch = !hasFetchedOnce.value
    if (isFirstFetch) loading.value = true
    try {
      await Promise.allSettled([
        fetchRepairStatus(),
        fetchServiceQuality(),
        fetchSlaCompliance(),
        fetchPartnerKpi(),
        fetchRegionHeatmap(),
        fetchTodaySummary(),
        fetchCustomerStatus()
      ])
    } catch (error) {
      window.__APP_LOG__(error)
    } finally {
      if (isFirstFetch) loading.value = false
      hasFetchedOnce.value = true
    }
  }

  // ── 워쳐 (Watchers) ──

  /**
   * 기간 프리셋 변경 시 자동으로 전체 데이터를 갱신합니다.
   * 사용자 지정 날짜 범위 모드일 때는 자동 갱신하지 않습니다.
   */
  watch(
    () => filter.periodStatus.value,
    () => {
      if (!filter.isCustomDateRange.value) {
        fetchAll()
      }
    }
  )

  return {
    repairStatus,
    serviceQuality,
    slaCompliance,
    partnerKpi,
    regionHeatmap,
    todaySummary,
    customerStatus,
    loading,
    fetchRepairStatus,
    fetchServiceQuality,
    fetchSlaCompliance,
    fetchPartnerKpi,
    fetchRegionHeatmap,
    fetchTodaySummary,
    fetchCustomerStatus,
    fetchAll
  }
}
